import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import AiLookDialog from './AiLookDialog';

function avatarImageUrl(url) {
  if (!url) return '';
  const value = String(url);
  if (value.startsWith('/media/') || value.startsWith('/outfit-detective/') || value.includes('/outfit-detective/')) return value;
  return `/api/media/avatar?url=${encodeURIComponent(url)}`;
}

function profileFallbackUrl(profile) {
  if (profile.profile_url) return profile.profile_url;
  if (profile.platform === 'tiktok') return `https://www.tiktok.com/@${profile.handle}`;
  return `https://www.instagram.com/${profile.handle}/`;
}

function ProfileSkeleton() {
  return (
    <div className="ct-profile-page">
      <section className="ct-profile-hero ct-profile-hero-skeleton">
        <div className="ct-profile-header">
          <span className="ct-profile-avatar ct-skeleton-block"></span>
          <div className="ct-profile-title">
            <span className="ct-skeleton-line short"></span>
            <span className="ct-skeleton-line title"></span>
            <span className="ct-skeleton-line medium"></span>
          </div>
        </div>
      </section>
      <section className="ct-profile-grid">
        <aside className="ct-profile-side">
          <div className="ct-soft-card">
            <span className="ct-skeleton-line medium"></span>
            <span className="ct-skeleton-line"></span>
            <span className="ct-skeleton-line"></span>
          </div>
        </aside>
        <main className="ct-profile-feed">
          <div className="ct-profile-event-grid">
            {[0, 1, 2, 3].map((item) => (
              <span className="ct-profile-event-card ct-profile-event-skeleton" key={item}>
                <span className="ct-profile-event-media"></span>
                <span className="ct-profile-event-copy">
                  <span className="ct-skeleton-line"></span>
                  <span className="ct-skeleton-line short"></span>
                </span>
              </span>
            ))}
          </div>
        </main>
      </section>
    </div>
  );
}

const FollowedProfile = () => {
  const { slug } = useParams();
  const { formatDate, formatNumber, t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [aiDialogEvent, setAiDialogEvent] = useState(null);
  const profileEventCount = profile?.events?.length || 0;
  const shouldPollProfile = Boolean(
    profile?.feed_fetch?.is_fetching ||
    (profile?.celebrity?.is_following && profileEventCount === 0),
  );

  const loadProfile = useCallback(async ({ showSkeleton = false, silent = false } = {}) => {
    if (showSkeleton) setLoading(true);
    if (!silent) setError('');

    try {
      const res = await api.getCelebrityProfile(slug);
      setProfile(res.data);
      return res.data;
    } catch (err) {
      if (!silent) setError(err.message);
      return null;
    } finally {
      if (showSkeleton) setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    setNotice('');
    loadProfile({ showSkeleton: true });
  }, [loadProfile, slug]);

  useEffect(() => {
    if (!shouldPollProfile) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = profile?.feed_fetch?.is_fetching ? 90 : 12;

    const timer = window.setInterval(async () => {
      attempts += 1;
      const nextProfile = await loadProfile({ showSkeleton: false, silent: true });
      if (cancelled) return;
      const stillFetching = Boolean(nextProfile?.feed_fetch?.is_fetching);
      const hasEvents = (nextProfile?.events || []).length > 0;
      if ((!stillFetching && hasEvents) || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 4000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [loadProfile, profile?.celebrity?.id, profile?.feed_fetch?.is_fetching, shouldPollProfile]);

  async function refreshFeed() {
    setRefreshing(true);
    setNotice('');
    setError('');

    try {
      const res = await api.refreshCelebrityFeed(slug);
      setProfile(res.data);
      setNotice(t('profile.feedUpdated'));
    } catch {
      setNotice(t('profile.feedRefreshFailed'));
    } finally {
      setRefreshing(false);
    }
  }

  async function toggleFollow() {
    if (!profile?.celebrity?.id) return;
    setNotice('');
    setError('');

    try {
      if (profile.celebrity.is_following) {
        await api.unfollowCelebrity(profile.celebrity.id);
        setProfile((current) => current ? {
          ...current,
          celebrity: { ...current.celebrity, is_following: false },
        } : current);
      } else {
        await api.followCelebrity(profile.celebrity.id);
        setProfile((current) => current ? {
          ...current,
          celebrity: { ...current.celebrity, is_following: true },
        } : current);
      }
    } catch (err) {
      setError(t('discover.removeError'));
    }
  }

  if (loading) return <ProfileSkeleton />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!profile) return null;

  const { celebrity, social_profiles: socialProfiles = [], events = [] } = profile;
  const mainProfile = socialProfiles[0];
  const canRefreshFeed = Boolean(profile.feed_refresh?.canManualRefresh);
  const isFeedFetching = refreshing || Boolean(profile.feed_fetch?.is_fetching);
  const dateOptions = { day: '2-digit', month: 'short', year: 'numeric' };

  return (
    <div className="ct-profile-page">
      <section className="ct-profile-hero">
        <div className="ct-profile-header">
          <span className="ct-profile-avatar">
            {celebrity.image_url ? <img src={avatarImageUrl(celebrity.image_url)} alt="" /> : null}
            <span>{(celebrity.name || '?').slice(0, 1).toUpperCase()}</span>
          </span>
          <div className="ct-profile-title">
            <p className="ct-kicker">{celebrity.is_following ? t('profile.followingStatus') : t('profile.notFollowingStatus')}</p>
            <h1>{celebrity.name}</h1>
            {mainProfile ? (
              <p>
                {mainProfile.platform} @{mainProfile.handle}
                {mainProfile.is_verified ? <i className="ph-fill ph-seal-check"></i> : null}
              </p>
            ) : null}
          </div>
          <div className="ct-profile-actions">
            {mainProfile ? (
              <a href={profileFallbackUrl(mainProfile)} target="_blank" rel="noreferrer">
                {t('discover.openProfile')}
              </a>
            ) : null}
            {canRefreshFeed ? (
              <button type="button" onClick={refreshFeed} disabled={refreshing}>
                {refreshing ? (
                  <>
                    <span className="ct-mini-spinner" aria-hidden="true"></span>
                    {t('profile.refreshingFeed')}
                  </>
                ) : (
                  <>
                    <i className="ph ph-arrows-clockwise"></i>
                    {t('profile.refreshFeed')}
                  </>
                )}
              </button>
            ) : null}
            <button type="button" onClick={toggleFollow}>
              {celebrity.is_following ? t('common.unfollow') : t('common.follow')}
            </button>
            <Link to="/discover">{t('feed.findProfiles')}</Link>
          </div>
        </div>
      </section>
      {notice ? <div className="ct-profile-notice">{notice}</div> : null}
      {isFeedFetching ? (
        <div className="ct-profile-sync-banner" role="status" aria-live="polite">
          <span className="ct-mini-spinner" aria-hidden="true"></span>
          <span>
            <strong>{t('profile.feedUpdatingTitle')}</strong>
            <em>{t('profile.feedUpdating')}</em>
          </span>
        </div>
      ) : null}

      <section className="ct-profile-grid">
        <aside className="ct-profile-side">
          <div className="ct-soft-card">
            <h2>Social</h2>
            <div className="ct-profile-social-list">
              {socialProfiles.map((item) => (
                <a href={profileFallbackUrl(item)} target="_blank" rel="noreferrer" key={item.id}>
                  <span>{item.platform}</span>
                  <strong>@{item.handle}</strong>
                  <em>
                    {item.follower_count ? t('discover.followers', { count: formatNumber(item.follower_count) }) : t('discover.followersUnavailable')}
                  </em>
                  {Number(profile.feed_fetch?.social_profile_id) === Number(item.id) ? (
                    <small className="ct-profile-social-sync">
                      <span className="ct-mini-spinner" aria-hidden="true"></span>
                      {t('profile.feedCollectingShort')}
                    </small>
                  ) : null}
                </a>
              ))}
              {socialProfiles.length === 0 ? <p className="ct-muted">No social profile.</p> : null}
            </div>
          </div>
        </aside>

        <main className="ct-profile-feed">
          {canRefreshFeed ? (
            <div className="ct-profile-feed-actions">
              <button type="button" className="ct-soft-action" onClick={refreshFeed} disabled={refreshing}>
                {refreshing ? <span className="ct-mini-spinner" aria-hidden="true"></span> : <i className="ph ph-arrows-clockwise"></i>}
                {refreshing ? t('profile.refreshingFeed') : t('common.refresh')}
              </button>
            </div>
          ) : null}

          {events.length === 0 ? (
            <div className="ct-card ct-empty">
              <h2>{t('feed.emptyTitle')}</h2>
            </div>
          ) : null}

          <div className="ct-profile-event-grid">
            {events.map((event) => (
              <article className="ct-profile-event-card" key={event.id}>
                <Link to={`/events/${event.id}`} className="ct-profile-event-link">
                  <span className="ct-profile-event-media">
                    {event.image_url ? <img src={event.image_url} alt="" /> : null}
                  </span>
                  <span className="ct-profile-event-copy">
                    <strong>{event.title}</strong>
                    <em>{event.source_name} · {formatDate(event.event_at, dateOptions)}</em>
                    <small>
                      {event.item_count} item · {event.product_count} shopping link
                    </small>
                  </span>
                </Link>
                <button type="button" className="ct-profile-ai-action" onClick={() => setAiDialogEvent(event)}>
                  <i className="ph ph-sparkle"></i>
                  <span>{t('feed.aiAction')}</span>
                </button>
              </article>
            ))}
          </div>
        </main>
      </section>
      <AiLookDialog event={aiDialogEvent} open={Boolean(aiDialogEvent)} onClose={() => setAiDialogEvent(null)} />
    </div>
  );
};

export default FollowedProfile;
